import { useEffect } from "react";

const isDocumentVisible = (): boolean =>
  typeof document === "undefined" || document.visibilityState === "visible";

/**
 * Calls `onBecameVisible` once each time the tab goes from hidden to visible, while `enabled`.
 *
 * Pollers gated by `useConditionalRefetchInterval` stop while the tab is hidden. When the tab
 * returns, React Query starts a fresh interval timer, so the first new result would land one
 * full interval later. Pair this hook with that gate to refresh right away instead, without
 * polling in the background and without turning on `refetchOnWindowFocus`.
 *
 * Window focus changes that leave the tab visible (DevTools, another app) do not trigger it.
 */
export const useRefetchOnPageVisible = (enabled: boolean, onBecameVisible: () => void): void => {
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return;

    let wasVisible = isDocumentVisible();
    const handleVisibilityChange = () => {
      const isVisible = isDocumentVisible();
      if (isVisible && !wasVisible) {
        onBecameVisible();
      }
      wasVisible = isVisible;
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, onBecameVisible]);
};
